import { Controller } from "@hotwired/stimulus"

// Settings onboarding banner: copies the agent setup command and lets the user
// dismiss the banner permanently (per browser, via localStorage).
//
// Usage:
//   div data-controller="agent-onboarding"
//       data-agent-onboarding-storage-key-value="bynn.settings.agent_onboarding.dismissed"
//     button data-action="agent-onboarding#dismiss"
//     code   data-agent-onboarding-target="source"
//     button data-action="agent-onboarding#copy" data-agent-onboarding-target="button"
//     span   data-agent-onboarding-target="status"
export default class extends Controller {
  static targets = ["source", "button", "status"]
  static values = {
    storageKey: { type: String, default: "bynn.settings.agent_onboarding.dismissed" },
    // Only the settings banner is dismissible. The agent settings page reuses
    // this controller purely for its copy button and must never self-remove.
    dismissible: { type: Boolean, default: false }
  }

  connect() {
    if (this.dismissibleValue && this.#isDismissed()) this.#removeBanner()
  }

  disconnect() {
    if (this.resetTimer) clearTimeout(this.resetTimer)
    if (this.removeTimer) clearTimeout(this.removeTimer)
  }

  copy() {
    const text = (this.sourceTarget.textContent || "").trim()
    if (!text) return

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => this.#flashCopied())
        .catch(() => this.#copyFallback(text))
    } else {
      this.#copyFallback(text)
    }
  }

  dismiss() {
    if (this.dismissing) return
    this.dismissing = true
    this.#rememberDismissal()

    if (this.#prefersReducedMotion() || !this.element.animate) {
      this.#removeBanner()
      return
    }

    const DURATION = 220
    const { height } = this.element.getBoundingClientRect()
    const styles = window.getComputedStyle(this.element)

    // Collapse padding alongside height, otherwise the banner bottoms out at
    // its own vertical padding instead of reaching zero.
    const animation = this.element.animate(
      [
        {
          opacity: 1,
          transform: "translateY(0)",
          height: `${height}px`,
          paddingTop: styles.paddingTop,
          paddingBottom: styles.paddingBottom
        },
        {
          opacity: 0,
          transform: "translateY(-6px)",
          height: "0px",
          paddingTop: "0px",
          paddingBottom: "0px"
        }
      ],
      { duration: DURATION, easing: "cubic-bezier(0.23, 1, 0.32, 1)", fill: "forwards" }
    )

    // Never rely on animation.finished: it can stay pending indefinitely while
    // the document is hidden or unfocused, which would leave a collapsed husk
    // of the banner on the page. onfinish plus a timeout guarantees removal.
    const remove = () => {
      if (this.removeTimer) clearTimeout(this.removeTimer)
      this.#removeBanner()
    }
    animation.onfinish = remove
    animation.oncancel = remove
    this.removeTimer = setTimeout(remove, DURATION + 100)
  }

  // --- private -------------------------------------------------------------

  // Takes the layout wrapper with it, otherwise its padding leaves a gap above
  // the settings grid once the banner itself is gone.
  #removeBanner() {
    const wrapper = this.element.parentElement
    this.element.remove()
    if (wrapper?.classList.contains("agent-onboard-wrap") && wrapper.childElementCount === 0) {
      wrapper.remove()
    }
  }

  #copyFallback(text) {
    const field = document.createElement("textarea")
    field.value = text
    field.setAttribute("readonly", "")
    field.style.position = "fixed"
    field.style.opacity = "0"
    document.body.appendChild(field)
    field.select()
    try {
      document.execCommand("copy")
      this.#flashCopied()
    } finally {
      field.remove()
    }
  }

  #flashCopied() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.buttonTarget?.dataset.copiedLabel || "Copied"
    }
    if (!this.hasButtonTarget) return

    this.buttonTarget.classList.add("is-copied")
    if (this.resetTimer) clearTimeout(this.resetTimer)
    this.resetTimer = setTimeout(() => {
      this.buttonTarget.classList.remove("is-copied")
      if (this.hasStatusTarget) this.statusTarget.textContent = ""
    }, 2000)
  }

  #isDismissed() {
    try {
      return window.localStorage.getItem(this.storageKeyValue) === "1"
    } catch {
      return false
    }
  }

  #rememberDismissal() {
    try {
      window.localStorage.setItem(this.storageKeyValue, "1")
    } catch {
      // Private mode or blocked storage: the banner simply returns next visit.
    }
  }

  #prefersReducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  }
}
